import React from 'react';
import { Card, CardBody, Table, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './DashboardStyles.css';
import { getImageUrl } from '../utils/imageUrl';

interface Player {
  _id: string;
  name: string;
  course: string;
  dateOfBirth: string;
  sport: string;
  contact: string;
  location: string;
  status: 'approved' | 'rejected' | 'pending';
  profilePic?: string;
}

interface PlayerStatusTableProps {
  players: Player[];
  viewAllLink: string;
}

const PlayerStatusTable: React.FC<PlayerStatusTableProps> = ({ players, viewAllLink }) => {
  const navigate = useNavigate();

  const getProfilePicUrl = (profilePic: string) => {
    if (!profilePic || profilePic === 'N/A' || profilePic === 'undefined' || profilePic === 'null') {
      return '/default-profile.png';
    }
    return getImageUrl(profilePic);
  };

  const formatDate = (dateString: string) => {
    // Handle null, undefined, or empty values
    if (!dateString || dateString === 'N/A' || dateString === 'undefined' || dateString === 'null') {
      return 'N/A';
    }
    
    // Handle age category format like "U 7" or "U 9"
    if (typeof dateString === 'string' && dateString.startsWith('U ')) {
      return dateString;
    }
    
    // Handle actual date format
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleDateString('en-US', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'N/A';
    }
  };

  const getStatusBadge = (status: string) => {
    // Use the same styling as player listing with verify-badge class
    switch (status) {
      case 'approved':
        return <Badge bg="success" className="verify-badge">Approved</Badge>;
      case 'referred_back':
        return <Badge bg="warning" className="verify-badge">Referred Back</Badge>;
      case 'pending':
        return <Badge bg="warning" className="verify-badge">Pending</Badge>;
      default:
        return <Badge bg="secondary" className="verify-badge">{status}</Badge>;
    }
  };

  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Recent Players Status</h5>
          <Button 
            variant="link" 
            className="text-decoration-none p-0"
            onClick={() => navigate(viewAllLink)}
          >
            View all <i className="ph-arrow-right ms-1"></i>
          </Button>
        </div>
        
        <div className="table-responsive global-table-responsive">
          <Table className="global-table">
            <thead>
              <tr>
                <th scope="col">Player Name</th>
                <th scope="col">Age Category</th>
                <th scope="col">DOB</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {players.length > 0 ? (
                players.slice(0, 5).map((player) => (
                  <tr key={player._id || `player-${Date.now()}`}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm me-3">
                          {player.profilePic ? (
                            <img
                              src={getProfilePicUrl(player.profilePic)}
                              alt={player.name || 'Player'}
                              className="avatar-title rounded-circle"
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                // Prevent infinite onError loop if default image is missing
                                if (!target.src.includes('/default-profile.png')) {
                                  target.src = '/default-profile.png';
                                } else {
                                  target.onerror = null;
                                }
                              }}
                            />
                          ) : (
                            <div className="avatar-title bg-primary-subtle rounded-circle">
                              <i className="ph-user text-primary"></i>
                            </div>
                          )}
                        </div>
                        <div>
                          <h6 className="mb-0">{player.name || 'N/A'}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {player.course || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {formatDate(player.dateOfBirth)}
                      </span>
                    </td>
                    <td>
                      {getStatusBadge(player.status || 'pending')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <i className="ph-users text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-muted mt-2 mb-0">No recent players</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerStatusTable; 